'use client'

import React from 'react'
import { CloseSquareBroken } from 'solar-icons'
import { revalidateCarts } from '@/lib/actions'
import { useCheckout, useSearchParams } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  LoadingDots,
} from '@/components/ui'

interface CancelCartProps extends React.ComponentPropsWithoutRef<'div'> {
  cartId: string
  toggle?: React.ReactNode
  callback?: () => void
}

export const CancelCart = ({ cartId, toggle, callback }: CancelCartProps) => {
  const { cancelCart } = useCheckout()
  const { isCheckout } = useSearchParams()
  const [isCancelling, setIsCancelling] = React.useState(false)

  const handleCancel = async () => {
    setIsCancelling(true)
    cancelCart(cartId)
    revalidateCarts()
    callback?.()
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {toggle ?? (
          <Button
            variant="secondary"
            className={cn(
              'hidden gap-2 lg:inline-flex lg:w-[144px]',
              isCheckout && 'h-10',
            )}
            size="sm"
            disabled={isCancelling}
          >
            {isCancelling && <LoadingDots />}
            {!isCancelling && (
              <>
                <CloseSquareBroken strokeWidth={1.2} className="size-5" />
                <span>Cancel Order</span>
              </>
            )}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Order?</AlertDialogTitle>
          <AlertDialogDescription>
            You are about to delete the current order. This action cannot be
            undone. Are you sure you want to proceed?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="w-full">Close</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isCancelling}
            onClick={handleCancel}
            className="w-full"
            autoFocus
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
